import { Modal } from "@flanksource-ui/ui/Modal";
import { Button } from "@flanksource-ui/ui/Buttons/Button";
import { Formik, Form } from "formik";
import { FaSpinner } from "react-icons/fa";
import FormikTextInput from "@flanksource-ui/components/Forms/Formik/FormikTextInput";
import FormikTextArea from "@flanksource-ui/components/Forms/Formik/FormikTextArea";
import ScopeTargetsForm from "./ScopeTargetsForm";
import DeleteRbacObjectButton from "@flanksource-ui/components/Permissions/Rbac/DeleteRbacObjectButton";
import {
  RbacObjectNotInEffect,
  RbacObjectStatus
} from "@flanksource-ui/components/Permissions/Rbac/RbacObjectStatus";
import { RbacObjectYaml } from "@flanksource-ui/components/Permissions/Rbac/RbacObjectYaml";
import { notifyRbacSaved } from "@flanksource-ui/components/Permissions/Rbac/notifyRbacSaved";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@flanksource-ui/components/ui/tabs";
import {
  useCreateScopeMutation,
  useUpdateScopeMutation
} from "@flanksource-ui/api/query-hooks/useScopesQuery";
import {
  ScopeDisplay,
  ScopeDB,
  ScopeTargetForm,
  ScopeResourceSelectorForm,
  ScopeTarget
} from "@flanksource-ui/api/types/scopes";
import {
  RBAC_DEFAULT_NAMESPACE,
  getRbacReadOnlyReason,
  toRbacManifest,
  validateRbacNamespace
} from "@flanksource-ui/api/types/rbacResources";
import { toastError } from "@flanksource-ui/components/Toast/toast";
import CanEditResource from "@flanksource-ui/components/Settings/CanEditResource";
import { AuthorizationAccessCheck } from "@flanksource-ui/components/Permissions/AuthorizationAccessCheck";
import { tables } from "@flanksource-ui/context/UserAccessContext/permissions";
import { JSONViewer } from "@flanksource-ui/ui/Code/JSONViewer";
import YAML from "yaml";

type ScopeFormProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: ScopeDisplay;
};

export default function ScopeForm({ isOpen, onClose, data }: ScopeFormProps) {
  const readOnlyReason = getRbacReadOnlyReason("scopes", data);
  const isReadOnly = !!readOnlyReason;

  const { mutate: create, isLoading: isCreating } = useCreateScopeMutation();
  const { mutate: update, isLoading: isUpdating } = useUpdateScopeMutation();

  const isLoading = isCreating || isUpdating;

  const handleSubmit = (values: Partial<ScopeDB>) => {
    const transformedTargets = values.targets?.map(
      (target: ScopeTargetForm) => {
        const transformTarget = (selector: ScopeResourceSelectorForm) => {
          if (!selector) return selector;

          // If wildcard is enabled, only send {name: "*"}
          if (selector.wildcard) {
            return { name: "*" };
          }

          const { wildcard, ...rest } = selector;

          // Remove all empty strings
          return Object.fromEntries(
            Object.entries(rest).filter(([_, value]) => value !== "")
          );
        };

        return Object.fromEntries(
          Object.entries(target)
            .filter(([_, selector]) => !!selector)
            .map(([kind, selector]) => [kind, transformTarget(selector)])
        );
      }
    );

    // The namespace and name of an existing scope can't change
    const manifest = toRbacManifest(
      "scopes",
      {
        name: data?.name ?? values.name!,
        namespace: data?.namespace ?? values.namespace
      },
      {
        description: values.description || undefined,
        targets: (transformedTargets ?? []) as ScopeTarget[]
      }
    );

    const mutationOptions = (verb: "created" | "updated") => ({
      onSuccess: (saved: ScopeDB) => {
        notifyRbacSaved("scopes", verb, saved);
        onClose();
      },
      onError: (error: unknown) => {
        toastError(error);
      }
    });

    if (data?.id) {
      update(manifest, mutationOptions("updated"));
    } else {
      create(manifest, mutationOptions("created"));
    }
  };

  const initialTargets = data?.targets
    ? data.targets.map((target: any) => {
        const convertSelector = (selector: any) => {
          if (!selector) return selector;

          // Check if this is a wildcard selector (name: "*" and no other fields)
          const isWildcard =
            selector.name === "*" &&
            !Object.entries(selector).some(
              ([key, value]) => key !== "name" && !!value
            );

          return { ...selector, wildcard: isWildcard };
        };

        return Object.fromEntries(
          Object.entries(target)
            .filter(([_, selector]) => !!selector)
            .map(([kind, selector]) => [kind, convertSelector(selector)])
        );
      })
    : [
        {
          config: {
            name: "",
            agent: "",
            tagSelector: "",
            wildcard: false
          }
        }
      ];

  return (
    <Modal
      title={data?.id ? "Edit Scope" : "Add Scope"}
      onClose={onClose}
      open={isOpen}
      bodyClass="flex flex-col w-full flex-1 h-full overflow-y-auto"
      helpLink="/reference/scopes"
    >
      <Formik<Partial<ScopeDB>>
        initialValues={{
          name: data?.name || "",
          namespace: data ? data.namespace || "" : RBAC_DEFAULT_NAMESPACE,
          description: data?.description || "",
          targets: initialTargets,
          source: data?.source || "UI"
        }}
        onSubmit={handleSubmit}
        validate={(values) => {
          const errors: any = {};

          // Validate name
          if (!values.name) {
            errors.name = "Name is required";
          }

          // The API requires a namespace that's a DNS label, outside the shipped one
          if (!data) {
            const namespaceError = validateRbacNamespace(values.namespace);
            if (namespaceError) {
              errors.namespace = namespaceError;
            }
          }

          // Validate targets
          if (!values.targets || values.targets.length === 0) {
            errors.targets = "At least one target is required";
          } else {
            // Validate each target has exactly one resource type
            values.targets.forEach((target: any, index: number) => {
              const resourceTypes = Object.values(target).filter(
                Boolean
              ) as ScopeResourceSelectorForm[];

              if (resourceTypes.length === 0) {
                if (!errors.targets) errors.targets = [];
                errors.targets[index] =
                  "Each target must specify exactly one resource type";
              } else if (resourceTypes.length > 1) {
                if (!errors.targets) errors.targets = [];
                errors.targets[index] =
                  "Each target can only have one resource type";
              } else {
                // Validate that the target is not empty
                const selectedResource = resourceTypes[0];
                const name = selectedResource.name || "";
                const agent = selectedResource.agent || "";
                const wildcard = selectedResource.wildcard || false;

                // If wildcard is enabled, validation passes
                const isEmpty =
                  !wildcard &&
                  name === "" &&
                  agent === "" &&
                  !selectedResource.id &&
                  !selectedResource.namespace &&
                  !selectedResource.types?.length &&
                  !selectedResource.labelSelector &&
                  !selectedResource.tagSelector;

                if (isEmpty) {
                  if (!errors.targets) errors.targets = [];
                  errors.targets[index] =
                    "Target cannot be empty. Please specify at least one of: name, agent, tags, or enable wildcard";
                }
              }
            });
          }

          return errors;
        }}
      >
        {({ isSubmitting, submitCount }) => {
          const fields = (
            <div className="flex flex-col space-y-3">
              {readOnlyReason && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                  <p className="font-medium">{readOnlyReason}</p>
                </div>
              )}

              {data && <RbacObjectNotInEffect {...data} />}

              <div
                className={isReadOnly ? "pointer-events-none opacity-60" : ""}
              >
                <FormikTextInput
                  name="name"
                  label="Name"
                  required
                  // The API identifies a scope by namespace and name, so it can't be renamed
                  disabled={isReadOnly || !!data}
                />
              </div>

              <div className={data ? "pointer-events-none opacity-60" : ""}>
                <FormikTextInput
                  name="namespace"
                  label="Namespace"
                  required
                  hint={
                    data
                      ? undefined
                      : "Roles and role bindings in any namespace can use the scope, as namespace/name."
                  }
                  // The API identifies a scope by namespace and name, so it can't be moved
                  disabled={!!data}
                />
              </div>

              <div
                className={isReadOnly ? "pointer-events-none opacity-60" : ""}
              >
                <FormikTextArea
                  name="description"
                  label="Description"
                  disabled={isReadOnly}
                />
              </div>

              {isReadOnly ? (
                <div className="flex flex-col gap-2">
                  <label className="form-label">Targets</label>
                  <div className="rounded border border-gray-300 bg-gray-50">
                    <JSONViewer
                      code={YAML.stringify(data?.targets || [])}
                      format="yaml"
                      showLineNo={false}
                    />
                  </div>
                </div>
              ) : (
                <ScopeTargetsForm
                  disabled={isReadOnly}
                  submitCount={submitCount}
                />
              )}
            </div>
          );

          return (
            <Form className="flex flex-1 flex-col gap-2 overflow-y-auto">
              <div className="flex flex-1 flex-col overflow-y-auto p-4">
                {data ? (
                  <Tabs defaultValue="form" className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <TabsList>
                        <TabsTrigger value="form">Form</TabsTrigger>
                        <TabsTrigger value="yaml">YAML</TabsTrigger>
                      </TabsList>
                      <RbacObjectStatus {...data} />
                    </div>
                    <TabsContent value="form">{fields}</TabsContent>
                    <TabsContent value="yaml">
                      <RbacObjectYaml
                        manifest={toRbacManifest("scopes", data, {
                          description: data.description || undefined,
                          targets: data.targets
                        })}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  fields
                )}
              </div>

              <CanEditResource
                id={data?.id}
                resourceType="scopes"
                source={data?.source}
                namespace={data?.namespace ?? undefined}
                name={data?.name}
                className="flex items-center justify-between bg-gray-100 px-5 py-4"
              >
                {isReadOnly ? (
                  <div />
                ) : (
                  <>
                    <div>
                      {data?.namespace && (
                        <AuthorizationAccessCheck
                          resource={tables.scopes}
                          action="delete"
                        >
                          <DeleteRbacObjectButton
                            resource="scopes"
                            namespace={data.namespace}
                            name={data.name}
                            onDeleted={onClose}
                          />
                        </AuthorizationAccessCheck>
                      )}
                    </div>
                    <AuthorizationAccessCheck
                      resource={tables.scopes}
                      action={data?.id ? "update" : "create"}
                    >
                      <Button
                        type="submit"
                        text={data?.id ? "Save" : "Create"}
                        className="btn-primary"
                        icon={
                          isLoading ? (
                            <FaSpinner className="animate-spin" />
                          ) : undefined
                        }
                        disabled={isLoading || isSubmitting}
                      />
                    </AuthorizationAccessCheck>
                  </>
                )}
              </CanEditResource>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
